import { useEffect, useRef } from 'react';
import type { JawsSegmentationResponse } from '@/schema/jawsSegmentation';
import type { JawsCorrectionResponse } from '@/schema/JawsCorrection';
import { ExportScene } from '@export/three/ExportScene.ts';
import useExportStore from '@export/hooks/useExportStore.ts';
import useThreeStore from '@/modules/three/hooks/useThreeStore';
import LoadingPage from '@/components/ui/LoadingPage';

interface Props {
  segmentation: JawsSegmentationResponse;
  activeCorrection: JawsCorrectionResponse | null;
}

export function ExportCanvas({ segmentation, activeCorrection }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<ExportScene>();
  const sceneReadyRef = useRef(false);
  const latestCorrectionRef = useRef<JawsCorrectionResponse | null>(activeCorrection);
  latestCorrectionRef.current = activeCorrection ?? null;
  const { reset, setGenerateAlignerShell, setAlignerStatus } = useExportStore();
  const { isLoading: isThreeLoading } = useThreeStore();

  useEffect(() => {
    if (!containerRef.current) return;

    const scene = new ExportScene(containerRef.current);
    sceneRef.current = scene;
    sceneReadyRef.current = false;
    let disposed = false;

    (async () => {
      await scene.init(segmentation);
      if (disposed) return;
      await scene.displayCorrection(latestCorrectionRef.current ?? null);
      if (disposed) return;
      sceneReadyRef.current = true;
      setAlignerStatus({ state: 'idle' });
      setGenerateAlignerShell((options) => scene.generateAlignerShellFromUI(options));
    })();

    return () => {
      disposed = true;
      sceneReadyRef.current = false;
      scene.dispose();
      sceneRef.current = undefined;
      setGenerateAlignerShell(null);
      setAlignerStatus({ state: 'idle' });
      reset();
    };
  }, [segmentation.id, reset, setGenerateAlignerShell, setAlignerStatus]);

  useEffect(() => {
    if (!sceneReadyRef.current) return;
    if (!sceneRef.current) return;
    void sceneRef.current.displayCorrection(activeCorrection ?? null);
  }, [activeCorrection]);

  return (
    <div className="relative h-full w-full">
      {isThreeLoading && <LoadingPage loadingText="Loading export scene..." />}
      <div ref={containerRef} className="h-full w-full rounded-3xl" />
    </div>
  );
}

